import React from "react";
import {
  EmployeeOfficeList,
  RemoteAddressType,
  SingleCensusType,
  SingleCensusInformationTypeKey,
} from "types";
import { getMemberUuid } from "utils/idHelpers";

import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import Info from "pages/WorkersCompApplcation/formStepComponents/ReviewAndSubmit/components/Info";
import { getOfficeFullAddress } from "utils/helper";

interface ISavedCensusProps {
  data?: SingleCensusType;
  officeLocations?: EmployeeOfficeList;
}

const SavedCensus = ({ ...props }: ISavedCensusProps): JSX.Element => {
  const { data, officeLocations } = props;
  const isFirstUser = data?.uuid === getMemberUuid();

  if (!data) {
    return (
      <p>
        Please complete or delete the information in this card before moving
        forward.
      </p>
    );
  }

  const selectedOfficeLocation = officeLocations?.find(
    (location: any) => location.uuid === data?.officeLocationId
  );

  const labelsList = sectionsQuestions["census"].questions;
  const questionList = Object.entries(labelsList);
  const firstUserQuestionList = sectionsQuestions["census"].firstUserQuestions;

  const getAnswer = (key: string) => {
    const remoteAddressValue =
      data?.employeeInformation?.remoteAddress?.[
        key as keyof RemoteAddressType
      ];

    if (["lastName", "fullName", "middleName"].includes(key)) {
      return;
    }

    if (key === "firstName") {
      return `${data.firstName} ${data.middleName} ${data.lastName}`;
    }

    if (key === "officeLocationId" && selectedOfficeLocation) {
      return getOfficeFullAddress(selectedOfficeLocation);
    }

    if (key === "employeeType" && data.isCompanyOwner) {
      // we need this as we do not ask this question on FE for owners
      // but BE set this value for owners
      return;
    }

    if (key === "censusType") {
      return data.isCompanyOwner
        ? "Owner"
        : data.isCompanyEmployee
        ? "Employee"
        : "";
    }

    if (key === "hourlyRate" || key === "salary") {
      const rate =
        key === "salary"
          ? data?.employeeInformation?.salary
          : data?.employeeInformation?.hourlyRate;

      if (!rate || Math.floor(rate) === rate) {
        return rate;
      }

      return rate.toFixed(2);
    }

    if (data?.[key as keyof SingleCensusType]) {
      return data?.[key as keyof SingleCensusType];
    }

    if (remoteAddressValue) {
      return remoteAddressValue;
    }

    return data?.employeeInformation?.[key as SingleCensusInformationTypeKey];
  };

  return (
    <div data-testid="saved-census-list">
      {questionList.map(([key, value]) => {
        const answer = getAnswer(key);
        let questionText = value;

        // change you/they references based on user
        if (isFirstUser) {
          questionText =
            firstUserQuestionList[key as keyof typeof firstUserQuestionList] ||
            value;
        }

        if (key === "firstName") {
          questionText = "Full name";
        }

        if (["address2", "city", "state", "zip"].includes(key)) {
          return <React.Fragment key={key} />;
        }

        if (key === "address1" && answer) {
          const remoteAddress = data?.employeeInformation?.remoteAddress;

          const remoteAddressAnswer = remoteAddress
            ? getOfficeFullAddress(remoteAddress)
            : "";

          return (
            <>
              <Info
                questionFieldName="remoteAddress"
                key="remoteAddress"
                question="Remote location"
                answer={remoteAddressAnswer}
              />
            </>
          );
        }

        return (
          <Info
            questionFieldName={key}
            key={key}
            question={questionText}
            answer={answer}
          />
        );
      })}
    </div>
  );
};

export default SavedCensus;
